/**
 * QuizFiltersBar Component
 * Filter bar with search, course filter, and sort options
 */

import React from 'react';
import styled from 'styled-components';
import { FiBookOpen, FiSearch, FiSliders } from 'react-icons/fi';

const FiltersContainer = styled.div`
  display: grid;
  grid-template-columns: ${({ $compact }) => $compact ? '1fr' : '1fr auto auto'};
  gap: 8px;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  width: 100%;
  min-width: 0;
  
  @media (max-width: 900px) {
    grid-template-columns: ${({ $compact }) => $compact ? '1fr' : '1fr 1fr'};
    gap: 8px;
  }

  @media (max-width: 480px) {
    padding: 0;
    border-radius: 0;
    background: transparent;
  }
`;

const SearchBar = styled.div`
  position: relative;
  grid-column: auto;
  max-width: none;
  min-width: 0;
  
  @media (max-width: 900px) {
    grid-column: 1 / -1;
    max-width: none;
    width: 100%;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  width: 16px;
  height: 16px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0 12px 0 36px;
  border: 0.5px solid #d6d6d6;
  border-radius: 8px;
  font-size: 0.875rem;
  background-color: #fff;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  transition: var(--transition);
  height: 36px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: var(--background-primary);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const FilterGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: ${({ $inline }) => $inline ? '0' : '152px'};
  
  @media (max-width: 900px) {
    width: 100%;
    min-width: auto;
  }

  @media (max-width: 520px) {
    flex: 1 1 0;
    min-width: 0;
  }
`;

const FilterLabel = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary, var(--text-secondary));
`;

const FilterIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  display: inline-flex;
  color: var(--color-text-secondary, var(--text-secondary));
  pointer-events: none;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FilterSelect = styled.select`
  flex: 1;
  padding: 0 34px 0 34px;
  border: 0.5px solid #d6d6d6;
  border-radius: 8px;
  font-size: 13px;
  background-color: #fff;
  color: var(--text-primary);
  cursor: pointer;
  transition: var(--transition);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1.25em 1.25em;
  height: 36px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: var(--background-primary);
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

function QuizFiltersBar({ 
  searchTerm, 
  onSearchChange, 
  courseFilter, 
  onCourseFilterChange, 
  courses = [],
  sortBy,
  onSortChange,
  showSort = true,
  showSearch = true
}) {
  const compactFilters = !showSearch && !showSort;

  return (
    <FiltersContainer $compact={compactFilters}>
      {showSearch && (
        <SearchBar>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search quizzes..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search quizzes"
          />
        </SearchBar>
      )}
      
      <FilterGroup $inline={compactFilters}>
        <FilterIcon><FiBookOpen /></FilterIcon>
        <FilterLabel htmlFor="course-filter">Course</FilterLabel>
        <FilterSelect
          id="course-filter"
          value={courseFilter}
          onChange={(e) => onCourseFilterChange(e.target.value)}
          aria-label="Filter by course"
        >
          <option value="all">All Courses</option>
          <option value="active">Active Courses</option>
          <option value="inactive">Inactive Courses</option>
          {courses.length > 0 && (
            <>
              <option disabled>──────────</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.code} - {course.name}
                </option>
              ))}
            </>
          )}
        </FilterSelect>
      </FilterGroup>
      
      {showSort && (
        <FilterGroup>
          <FilterIcon><FiSliders /></FilterIcon>
          <FilterLabel htmlFor="sort-filter">Sort</FilterLabel>
          <FilterSelect
            id="sort-filter"
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            aria-label="Sort quizzes"
          >
            <option value="recent">Recently updated</option>
            <option value="created">Created date</option>
            <option value="title">Title A–Z</option>
          </FilterSelect>
        </FilterGroup>
      )}
    </FiltersContainer>
  );
}

export default QuizFiltersBar;
